import type { LibraryRelease } from './exerciseLibraryApi';
import type { PublishedExercise } from './curatedExercisesApi';
import type { Actor, Step } from '../drilleditor/types';

export type OfflineTrainingExercise = {
  id: string;
  exerciseId?: string;
  title: string;
  drill: {
    id: string;
    title: string;
    duration?: number;
    categories: string[];
    tags: string[];
    introduction?: string;
    actors: Actor[];
    timeline: Step[];
    notes: string[];
  };
  savedAt: string;
  curated?: PublishedExercise;
  library?: LibraryRelease;
};

export type OfflineExerciseSource = {
  id: string;
  drill_id?: string | null;
  title: string | null;
  duration: number | null;
  categories: unknown;
  tags: unknown;
  introduction: string | null;
  actors: unknown;
  timeline: unknown;
  notes: unknown;
};

const DB_NAME = 'waedo-offline-db';
const DB_VERSION = 1;
const STORE_EXERCISES = 'offline_exercises';

const asStrings = (value: unknown): string[] => Array.isArray(value)
  ? value.map(item => String(item)).filter(Boolean)
  : [];

const openDb = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    if (!request.result.objectStoreNames.contains(STORE_EXERCISES)) {
      request.result.createObjectStore(STORE_EXERCISES, { keyPath: 'id' });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const withStore = async <T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => Promise<T>): Promise<T> => {
  const db = await openDb();
  const transaction = db.transaction(STORE_EXERCISES, mode);
  try {
    const result = await run(transaction.objectStore(STORE_EXERCISES));
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
    return result;
  } finally {
    db.close();
  }
};

export const saveOfflineTrainingExercise = async (source: OfflineExerciseSource): Promise<OfflineTrainingExercise> => {
  const record: OfflineTrainingExercise = {
    id: source.id,
    exerciseId: source.id,
    title: source.title?.trim() || 'Naamloze oefening',
    drill: {
      id: source.drill_id || source.id,
      title: source.title?.trim() || 'Naamloze oefening',
      duration: source.duration ?? undefined,
      categories: asStrings(source.categories),
      tags: asStrings(source.tags),
      introduction: source.introduction ?? undefined,
      actors: Array.isArray(source.actors) ? source.actors as Actor[] : [],
      timeline: Array.isArray(source.timeline) ? source.timeline as Step[] : [],
      notes: asStrings(source.notes),
    },
    savedAt: new Date().toISOString(),
  };
  await withStore('readwrite', store => new Promise<void>((resolve, reject) => {
    const request = store.put(record);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  }));
  return record;
};

export const getOfflineTrainingExercise = async (id: string): Promise<OfflineTrainingExercise | null> => {
  if (!id) return null;
  return withStore('readonly', store => new Promise((resolve, reject) => {
    const request = store.get(id);
    request.onsuccess = () => resolve((request.result as OfflineTrainingExercise | undefined) || null);
    request.onerror = () => reject(request.error);
  }));
};

export const listOfflineTrainingExercises = async (includeCurated = false): Promise<OfflineTrainingExercise[]> => withStore(
  'readonly',
  store => new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(((request.result as OfflineTrainingExercise[]) || []).filter(row => includeCurated || (!row.curated && !row.library)).sort((a, b) => b.savedAt.localeCompare(a.savedAt)));
    request.onerror = () => reject(request.error);
  }),
);

export const removeOfflineTrainingExercise = async (id: string): Promise<void> => {
  if (!id) return;
  await withStore('readwrite', store => new Promise<void>((resolve, reject) => {
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  }));
};

/** Explicit public revision copy: retain bilingual content, attribution, pitch and identity. */
export const saveOfflineCuratedExercise = async (source: PublishedExercise): Promise<void> => {
  const exercise = source.content.exercise;
  const record: OfflineTrainingExercise = {
    id: `curated:${source.key}:${source.source_revision}`, title: exercise.content.en.title,
    drill: { id: exercise.key, title: exercise.content.en.title, duration: exercise.durationMinutes,
      actors: exercise.actors, timeline: exercise.steps, categories: [], tags: [], notes: [] },
    curated: structuredClone(source), savedAt: new Date().toISOString(),
  };
  await withStore('readwrite', store => new Promise<void>((resolve, reject) => {
    const request = store.put(record);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  }));
};
export const listOfflineCuratedExercises = async (): Promise<PublishedExercise[]> =>
  (await listOfflineTrainingExercises(true)).flatMap(row => row.curated ? [row.curated] : []);

export const saveOfflineLibraryExercise = async (source: LibraryRelease): Promise<void> => {
  if (source.audience !== 'public' || !source.reuse.offline) throw new Error('Offline use is not permitted');
  const exercise = source.content.exercise;
  const title = exercise.content.en?.title || exercise.content.nl?.title || exercise.key;
  const record: OfflineTrainingExercise = { id: `library:${source.id}`, title,
    drill: { id: exercise.key, title, actors: exercise.actors, timeline: exercise.steps, categories: [], tags: [], notes: [] },
    library: structuredClone(source), savedAt: new Date().toISOString() };
  await withStore('readwrite', store => new Promise<void>((resolve, reject) => {
    const request = store.put(record); request.onsuccess = () => resolve(); request.onerror = () => reject(request.error);
  }));
};
export const listOfflineLibraryExercises = async (): Promise<LibraryRelease[]> =>
  (await listOfflineTrainingExercises(true)).flatMap(record => record.library ? [record.library] : []);
